/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import axios from "axios";
import Footer from "../components/Footer";
import { motion } from "framer-motion";
import {
  FaAward,
  FaSpinner,
  FaExternalLinkAlt
} from "react-icons/fa";

const Ourinitiatives = () => {
  const [dynamicInitiatives, setDynamicInitiatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = `${process.env.REACT_APP_API_BASE_URL}/UIourInitiatives`;
  const API_MEDIA = `${process.env.REACT_APP_API_BASE_URL}/UImedia`;
  const token = localStorage.getItem("token");

  // Fetch image with authentication
  const fetchImageWithAuth = async (mediaId) => {
    if (!mediaId) return null;
    try {
      const res = await axios.get(`${API_MEDIA}/${mediaId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      return URL.createObjectURL(res.data);
    } catch (error) {
      console.error("Error fetching image:", error);
      return null;
    }
  };

  // Fetch dynamic initiatives from API
  const fetchDynamicInitiatives = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const res = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const data = res.data.results || res.data || [];
      
      // Process dynamic initiatives with images
      const initiativesWithImages = await Promise.all(
        data.map(async (initiative) => {
          let iconUrl = null;
          let cardsWithImages = [];

          // Fetch icon image
          if (initiative.icon?.id) {
            iconUrl = await fetchImageWithAuth(initiative.icon.id);
          }

          // Fetch card images
          if (initiative.cards && initiative.cards.length > 0) {
            cardsWithImages = await Promise.all(
              initiative.cards.map(async (card) => {
                let cardImageUrl = null;
                if (card.image?.id) {
                  cardImageUrl = await fetchImageWithAuth(card.image.id);
                }
                return { 
                  ...card, 
                  imageUrl: cardImageUrl 
                };
              })
            );
          }

          return { 
            ...initiative, 
            iconUrl,
            cards: cardsWithImages,
            isDynamic: true // Mark as dynamic for rendering
          };
        })
      );

      setDynamicInitiatives(initiativesWithImages);
    } catch (err) {
      console.error("Fetch failed", err);
      setError("Failed to load initiatives. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDynamicInitiatives();
  }, []);

  // Combine static and dynamic initiatives
  const allInitiatives = [ ...dynamicInitiatives];

  // Default icon component for dynamic initiatives
  const DefaultIcon = ({ className = "text-yellow-400 text-4xl" }) => (
    <div className={className}>
      <FaAward />
    </div>
  );

  // Render icon for initiative
  const renderInitiativeIcon = (initiative, index) => {
    if (!initiative.isDynamic) {
      // Static initiative - use React component icon
      return initiative.icon;
    }
    
    // Dynamic initiative - use image URL or default icon
    if (initiative.iconUrl) {
      return (
        <img 
          src={initiative.iconUrl} 
          alt={`${initiative.title} icon`}
          className="w-20 h-20 object-contain"
        />
      );
    }
    
    return <DefaultIcon />;
  };

  // Render card image
  const renderCardImage = (card, initiative) => {
    if (initiative.isDynamic) {
      // Dynamic initiative - use imageUrl from API
      return card.imageUrl ? (
        <img
          src={card.imageUrl}
          alt={card.title}
          className="w-full h-60 object-cover"
        />
      ) : (
        <div className="w-full h-60 bg-gray-700 flex items-center justify-center">
          <DefaultIcon className="text-gray-500 text-3xl" />
        </div>
      );
    } else {
      // Static initiative - use direct image URL
      return (
        <img
          src={card.image}
          alt={card.title}
          className="w-full h-60 object-cover"
        />
      );
    }
  };

  // Handle special links for specific initiatives
  const getCardLink = (card, initiativeTitle) => {
    const isPowerBrand = initiativeTitle === "My Power Brand";
    const isNiveshManthan = initiativeTitle === "Nivesh Manthan";
    const isMSMETimes = initiativeTitle === "MSME Times";

    if (isPowerBrand) {
      return "https://www.youtube.com/results?search_query=my+power+brand";
    } else if (isNiveshManthan) {
      return "https://docs.google.com/forms/d/e/1FAIpQLSe7E-YehACGn_K8neq4HDZ4PseBDMhQs2SmEn_aHdju7ymOSA/viewform";
    } else if (isMSMETimes && card.title === "Join Our WhatsApp Channel") {
      return "https://whatsapp.com/channel/0029VbAqsGzD8SE05iGcWq28";
    }
    
    return card.link || null;
  };

  if (loading && dynamicInitiatives.length === 0) {
    return (
      <div className="min-h-screen bg-gray-800 text-white flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-yellow-400 text-4xl mx-auto mb-4" />
          <p className="text-gray-300">Loading initiatives...</p>
        </div>
      </div>
    );
  }

  if (error && dynamicInitiatives.length === 0) {
    return (
      <div className="min-h-screen bg-gray-800 text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-xl mb-4">{error}</p>
          <button 
            onClick={fetchDynamicInitiatives}
            className="bg-yellow-400 text-gray-800 font-bold py-2 px-6 rounded-full hover:bg-yellow-500 transition-colors duration-300"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-800 text-white">
      {/* Top Section */}
      <div className="text-center py-16 px-6">
        <motion.h1 
          className="text-5xl font-extrabold text-yellow-400 mb-4"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Our Initiatives
        </motion.h1>
        <motion.p 
          className="text-gray-300 text-lg max-w-2xl mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Empowering startups and MSMEs through impactful programs and platforms.
        </motion.p>
      </div>

      {/* Initiatives */}
      {allInitiatives.map((initiative, index) => (
        <div key={initiative.id || index} className="py-16 px-6 max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="flex justify-center mb-4">
              {renderInitiativeIcon(initiative, index)}
            </div>
            <h2 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600 mb-4">
              {initiative.title}
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto mb-4"></div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {initiative.description}
            </p>
            
            {/* Status badge for dynamic initiatives */}
            {/* {initiative.isDynamic && (
              <div className="mt-4">
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${
                  initiative.isActive 
                    ? "bg-green-500 text-white" 
                    : "bg-gray-500 text-white"
                }`}>
                  {initiative.isActive ? "ACTIVE" : "INACTIVE"}
                </span>
              </div>
            )} */}
          </motion.div>

          {/* Cards */}
          {initiative.cards && initiative.cards.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {initiative.cards.map((card, cardIndex) => {
                const link = getCardLink(card, initiative.title);

                const imageElement = renderCardImage(card, initiative);

                return (
                  <motion.div
                    key={cardIndex}
                    className="bg-gray-800/50 backdrop-blur-lg rounded-2xl overflow-hidden shadow-2xl hover:scale-105 transform transition-all duration-300 border border-gray-700/50"
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: cardIndex * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.03 }}
                  >
                    {link ? (
                      <a href={link} target="_blank" rel="noopener noreferrer" className="relative block">
                        {imageElement}
                        <div className="absolute top-2 right-2 bg-yellow-400 text-gray-800 p-1 rounded-full">
                          <FaExternalLinkAlt size={12} />
                        </div>
                      </a>
                    ) : (
                      imageElement
                    )}
                    <div className="p-6">
                      <h3 className="text-2xl font-bold text-yellow-400 mb-3">{card.title}</h3>
                      <p className="text-gray-300">{card.description}</p>
                      {card.link && !link && (
                        <div className="mt-3">
                          <a 
                            href={card.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-yellow-400 hover:text-yellow-300 text-sm flex items-center gap-1"
                          >
                            <FaExternalLinkAlt size={12} />
                            Learn More
                          </a>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 border-2 border-dashed border-gray-600 rounded-2xl">
              <DefaultIcon className="text-gray-500 text-4xl mx-auto mb-3" />
              <p className="text-gray-400 text-lg">No cards available for this initiative</p>
            </div>
          )}

          {/* Know More Button for MASA Awards */}
          {initiative.title === "MASA Awards" && (
            <div className="text-center mt-8">
              <a
                href="https://masaawards.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-yellow-400 text-gray-800 font-bold py-3 px-6 rounded-full hover:bg-yellow-500 transition-colors duration-300"
              >
                Know More
              </a>
            </div>
          )}
        </div>
      ))}

      {/* No Initiatives Message */}
      {allInitiatives.length === 0 && !loading && (
        <div className="text-center py-20">
          <DefaultIcon className="text-gray-500 text-6xl mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-gray-400 mb-2">No Initiatives Available</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            There are no initiatives to display at the moment.
          </p>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Ourinitiatives;


// old code 

// import React from "react";
// import Footer from "../components/Footer";
// import { motion } from "framer-motion";
// import {
//   FaAward,
//   FaLightbulb,
//   FaNewspaper,
//   FaHandshake,
//   FaChartLine,
//   FaCalendarAlt
// } from "react-icons/fa";

// // Data
// const initiatives = [
//   {
//     title: "MASA Awards",
//     description: "Recognizing and honoring the achievements of startups and MSMEs across India in various categories.",
//     icon: <FaAward className="text-yellow-400 text-4xl" />,
//     cards: [
//       { image: "https://res.cloudinary.com/dnmlijyox/image/upload/v1758179563/awards-imgs/masa-award-img.webp", title: "Startup of the Year", description: "Awarded to the most innovative startup." },
//       { image: "https://res.cloudinary.com/dnmlijyox/image/upload/awards-imgs/2024.webp", title: "MSME Leader", description: "Recognizing outstanding MSME leaders." },
//       { image: "https://res.cloudinary.com/dnmlijyox/image/upload/awards-imgs/2025.JPG.webp", title: "Innovation Excellence", description: "Celebrating technological innovation." },
//     ]
//   },
//   {
//     title: "My Power Brand",
//     description: "Showcasing the most influential emerging startups and MSMEs in India through comprehensive profiling and recognition.",
//     icon: <FaLightbulb className="text-yellow-400 text-4xl" />,
//     cards: [
//       { image: "https://res.cloudinary.com/dnmlijyox/image/upload/awards-imgs/power-brand-img.webp", title: "My Power Brand Podcasts", description: "Our podcast is a treasure trove of magical mantras that can turn your life around." },
//       { image: "https://res.cloudinary.com/dnmlijyox/image/upload/awards-imgs/powerBrand1.JPG.webp", title: "Podcast shoot Day-1", description: "Ft Twirl Store Founder Sujata Chatterjee" },
//       { image: "https://res.cloudinary.com/dnmlijyox/image/upload/awards-imgs/powerBrand2.webp", title: "Podcast shoot Day-6", description: "Ft Renowned matchmaker Kusum Bhandari" },
//     ]
//   },
//   {
//     title: "MSME Times",
//     description: "Quarterly digital magazine covering success stories, government schemes, and the latest business trends for MSMEs.",
//     icon: <FaNewspaper className="text-yellow-400 text-4xl" />,
//     cards: [
//       { image: "https://res.cloudinary.com/dnmlijyox/image/upload/awards-imgs/MSME-times-img.webp", title: "Market Insights", description: "Trends shaping the future." },
//       { image: "https://res.cloudinary.com/dnmlijyox/image/upload/awards-imgs/timesmsme.png", title: "Join Our WhatsApp Channel", description: " Click image to join whatsapp channel " },
//     ]
//   },
//   {
//     title: "MSME Kalptaru",
//     description: "Funding and mentorship initiative designed to empower MSMEs with resources for exponential growth.",
//     icon: <FaChartLine className="text-yellow-400 text-4xl" />,
//     cards: [
//       { image: "https://res.cloudinary.com/dnmlijyox/image/upload/awards-imgs/kalpataru.webp", title: "Funding Access", description: "Connecting startups to investors." },
//       { image: "https://res.cloudinary.com/dnmlijyox/image/upload/awards-imgs/kalpataru2.webp", title: "Mentorship Programs", description: "Guidance from industry veterans." },
//     ]
//   },
//   {
//     title: "Nivesh Manthan",
//     description: "Platform connecting promising startups with visionary investors through curated pitching opportunities.",
//     icon: <FaHandshake className="text-yellow-400 text-4xl" />,
//     cards: [
//       { image: "https://res.cloudinary.com/dnmlijyox/image/upload/awards-imgs/niveshmanthan.png", title: "Investor Connect", description: "Bringing founders & investors together." },
//       { image: "https://res.cloudinary.com/dnmlijyox/image/upload/awards-imgs/raipur-2.webp", title: "Pitch Days", description: "Live pitching events for startups." },
//       { image: "https://res.cloudinary.com/dnmlijyox/image/upload/awards-imgs/raipur9.webp", title: "VC Panels", description: "Insights from venture capitalists." },
//     ]
//   },
//   {
//     title: "Events & Initiatives",
//     description: "Workshops, exhibitions, conclaves, and training programs conducted across India for ecosystem upliftment.",
//     icon: <FaCalendarAlt className="text-yellow-400 text-4xl" />,
//     cards: [
//       { image: "https://res.cloudinary.com/dnmlijyox/image/upload/event-imgs/upcoming1.webp", title: "Raipur Conclave 2025", description: "" },
//       { image: "https://res.cloudinary.com/dnmlijyox/image/upload/event-imgs/upcoming1.webp", title: "East India Job & Career Expo", description: "" },
//       { image: "https://res.cloudinary.com/dnmlijyox/image/upload/event-imgs/upcoming1.webp", title: "Environment Quizz", description: "" },
//     ]
//   }
// ];

// const Ourinitiatives = () => {
//   return (
//     <div className="min-h-screen bg-gray-800 text-white">
//       {/* Top Section */}
//       <div className="text-center py-16 px-6">
//         <h1 className="text-5xl font-extrabold text-yellow-400 mb-4">Our Initiatives</h1>
//         <p className="text-gray-300 text-lg max-w-2xl mx-auto">
//           Empowering startups and MSMEs through impactful programs and platforms.
//         </p>
//       </div>

//       {/* Initiatives */}
//       {initiatives.map((initiative, index) => (
//         <div key={index} className="py-16 px-6 max-w-7xl mx-auto">
//           <motion.div
//             className="text-center mb-16"
//             initial={{ opacity: 0, y: 50 }}
//             whileInView={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.8 }}
//             viewport={{ once: true }}
//           >
//             <div className="flex justify-center mb-4">{initiative.icon}</div>
//             <h2 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600 mb-4">
//               {initiative.title}
//             </h2>
//             <div className="w-24 h-1 bg-yellow-500 mx-auto mb-4"></div>
//             <p className="text-xl text-gray-300 max-w-3xl mx-auto">
//               {initiative.description}
//             </p>
//           </motion.div>

//           {/* Cards */}
//           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
//             {initiative.cards.map((card, cardIndex) => {
//               const isPowerBrand = initiative.title === "My Power Brand";
//               const isNiveshManthan = initiative.title === "Nivesh Manthan";
//               const isMSMETimes = initiative.title === "MSME Times";
//               const link =
//                 isPowerBrand
//                   ? "https://www.youtube.com/results?search_query=my+power+brand"
//                   : isNiveshManthan
//                     ? "https://docs.google.com/forms/d/e/1FAIpQLSe7E-YehACGn_K8neq4HDZ4PseBDMhQs2SmEn_aHdju7ymOSA/viewform"
//                     : isMSMETimes
//                       ? "https://whatsapp.com/channel/0029VbAqsGzD8SE05iGcWq28"
//                       : null;

//               const imageElement = (
//                 <img
//                   src={card.image}
//                   alt={card.title}
//                   className="w-full h-60 object-cover"
//                 />
//               );

//               return (
//                 <motion.div
//                   key={cardIndex}
//                   className="bg-gray-800/50 backdrop-blur-lg rounded-2xl overflow-hidden shadow-2xl hover:scale-105 transform transition-all duration-300 border border-gray-700/50"
//                   initial={{ opacity: 0, y: 50 }}
//                   whileInView={{ opacity: 1, y: 0 }}
//                   transition={{ duration: 0.6, delay: cardIndex * 0.1 }}
//                   viewport={{ once: true }}
//                   whileHover={{ scale: 1.03 }}
//                 >
//                   {link ? (
//                     <a href={link} target="_blank" rel="noopener noreferrer">
//                       {imageElement}
//                     </a>
//                   ) : (
//                     imageElement
//                   )}
//                   <div className="p-6">
//                     <h3 className="text-2xl font-bold text-yellow-400 mb-3">{card.title}</h3>
//                     <p className="text-gray-300">{card.description}</p>
//                   </div>
//                 </motion.div>
//               );
//             })}
//           </div>

//           {/* Know More Button for MASA Awards */}
//           {initiative.title === "MASA Awards" && (
//             <div className="text-center mt-8">
//               <a
//                 href="https://masaawards.com"
//                 target="_blank"
//                 rel="noopener noreferrer"
//                 className="inline-block bg-yellow-400 text-gray-800 font-bold py-3 px-6 rounded-full hover:bg-yellow-500 transition-colors duration-300"
//               >
//                 Know More
//               </a>
//             </div>
//           )}
//         </div>
//       ))}

//       <Footer />
//     </div>
//   );
// };

// export default Ourinitiatives;
